import { ONBOARDING_COPY } from '@/locales/en'
import type { UsagePolicy } from '@/common/types'

export type ResolvedCheckboxItem = {
    id: string
    label: string
    updatedAt?: string
}

export type ResolvedSection = {
    id: string
    title: string
    description?: string
    checkboxes: ResolvedCheckboxItem[]
    slugs: string[]
}

export type ResolvedTermsSections = {
    sections: ResolvedSection[]
    legacyPolicies: UsagePolicy[]
}

/** Hardcoded content (title/description/checkbox come from locale, not the API's disclaimer/
 *  checkboxDisclaimer fields) — only shown when BOTH finclear-terms and cashhub-terms are
 *  present. Otherwise those policies fall through to legacyPolicies unchanged. */
function buildFinclearCashHubSection(policies: UsagePolicy[]): ResolvedSection | null {
    const cashhubPolicy = policies.find((policy) => policy.slug === 'cashhub-terms')

    if (!cashhubPolicy) return null

    const { finclearCashhub } = ONBOARDING_COPY.termsAndConditions.sections

    return {
        id: 'finclear-cashhub',
        title: finclearCashhub.title,
        description: finclearCashhub.description,
        checkboxes: [
            { id: 'finclear-cashhub', label: cashhubPolicy.disclaimer, updatedAt: cashhubPolicy.updatedAt },
            { id: 'finclear-cashhub-vrn', label: finclearCashhub.checkboxLabel },
            { id: 'finclear-nominated-bank', label: finclearCashhub.checkboxLabel2 },
        ],
        slugs: ['cashhub-terms'],
    }
}

/** Title/description are hardcoded from locale — only the checkbox comes from the actual
 *  monoova-terms policy returned by the request. */
function buildMonoovaSection(policies: UsagePolicy[]): ResolvedSection | null {
    const monoovaPolicy = policies.find((policy) => policy.slug === 'monoova-terms')
    if (!monoovaPolicy) return null

    const { monoova } = ONBOARDING_COPY.termsAndConditions.sections

    return {
        id: 'monoova',
        title: monoova.title,
        description: monoova.description,
        checkboxes: [{ id: 'monoova', label: monoovaPolicy.disclaimer, updatedAt: monoovaPolicy.updatedAt }],
        slugs: ['monoova-terms'],
    }
}

/** Multi-checkbox section: the FinClear terms checkbox (dynamic) always shows once
 *  finclear-terms is present; the CashHub terms checkbox (dynamic) and the hardcoded
 *  bank-account authorisation checkbox only appear alongside it when cashhub-terms is
 *  also present. Each checkbox tracks its own independent checked state. */
function buildFinclearConsentSection(policies: UsagePolicy[]): ResolvedSection | null {
    const finclearPolicy = policies.find((policy) => policy.slug === 'finclear-terms')
    if (!finclearPolicy) return null

    const { finclear } = ONBOARDING_COPY.termsAndConditions.sections

    const checkboxes: ResolvedCheckboxItem[] = [
        {
            id: 'finclear-terms-checkbox',
            label: finclearPolicy.disclaimer,
            updatedAt: finclearPolicy.updatedAt,
        },
        {
            id: 'finclear-chess-sponsorship',
            label: finclear.checkboxLabel,
        },
    ]

    return {
        id: 'finclear-consent',
        title: finclear.title,
        description: finclear.description,
        checkboxes,
        slugs: ['finclear-terms'],
    }
}

/** Pure grouping/title-resolution logic — takes API policies, returns display-ready sections.
 *  Any slug not consumed by a section (hardcoded or policy-driven) falls through to
 *  legacyPolicies so it still renders. */
export function resolveTermsSections(policies: UsagePolicy[]): ResolvedTermsSections {
    const finclearCashHubSection = buildFinclearCashHubSection(policies)
    const monoovaSection = buildMonoovaSection(policies)
    const finclearConsentSection = buildFinclearConsentSection(policies)
    const sections: ResolvedSection[] = []
    if (finclearConsentSection) sections.push(finclearConsentSection)
    if (finclearCashHubSection) sections.push(finclearCashHubSection)
    if (monoovaSection) sections.push(monoovaSection)

    const consumedSlugs = new Set<string>(sections.flatMap((section) => section.slugs))
    // The backend keeps returning six-terms for backward compatibility, but no section renders
    // it any more. Treat it as consumed (not legacy) so useTermsAndConditionsSections doesn't
    // report it via reportError as a policy that wasn't shown to the user.
    consumedSlugs.add('six-terms')
    const legacyPolicies = policies.filter((policy) => !consumedSlugs.has(policy.slug))

    return { sections, legacyPolicies }
}
